/* eslint-disable no-nested-ternary */
import { useEffect, useMemo } from 'react';
import {
  Box, SvgIcon,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/HomeOutlined';
import { t } from 'i18next';
import FeedOutlinedIcon from '@mui/icons-material/FeedOutlined';
import FeedIcon from '@mui/icons-material/Feed';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import PartnerIcon from '@mui/icons-material/HandshakeOutlined';
import FilledPartnerIcon from '@mui/icons-material/Handshake';
import FilledHomeIcon from '@mui/icons-material/Home';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import FilledAccountIcon from '@mui/icons-material/Person';
import WalletIcon from '@mui/icons-material/WalletOutlined';
import FilledWalletIcon from '@mui/icons-material/Wallet';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import EventIcon from '@mui/icons-material/Event';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import GroupsIcon from '@mui/icons-material/Groups';
import DesignServicesOutlinedIcon from '@mui/icons-material/DesignServicesOutlined';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import SchoolIcon from '@mui/icons-material/School';
import OndemandVideoOutlinedIcon from '@mui/icons-material/OndemandVideoOutlined';
import AssistantOutlinedIcon from '@mui/icons-material/AssistantOutlined';
import AssistantIcon from '@mui/icons-material/Assistant';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ArticleIcon from '@mui/icons-material/Article';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import SettingsIcon from '@mui/icons-material/Settings';
import SupportIcon from '@mui/icons-material/Support';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import LinkIcon from '@mui/icons-material/Link';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import SupportOutlinedIcon from '@mui/icons-material/SupportOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HelpIcon from '@mui/icons-material/Help';
import { useLocation, useNavigate } from 'react-router-dom';
import { JOIST_DOMAIN } from '@optimus/utils/src/constant';
import Decimal from 'decimal.js';
import { ServiceNationSolutionOrgName } from '../../common/constants';
import {
  ServiceNationCommunityLinkFlag,
  MembershipsPageFlag,
  ServiceNationEventsLinkFlag,
  ServiceNationDesignServicesLinkFlag,
  ServiceNationOnDemandTrainingLinkFlag,
  IframeSNEventTrainingPageFlag,
  IframeSNDesignServicesPageFlag,
  SNSettingsLinkFlag,
  ServiceNationAdvisoryBoardsLinkFlag,
  AiCoachFlag,
  ServiceNationWalletFlag,
  UseEdgeHomeForServiceNationUsersFlag,
  EdgeHomeNewDesignFlag,
} from '../../common/OptimizelyFlagsConstants';
import { DashboardSubMenuItemsProps, DashboardDesktopLayout } from './Home/DashboardDesktopLayout';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getTopTopics } from '../../store/thunks/contentThunk';
import { logout } from '../../store/thunks/authenticationThunk';
import {
  NavigationTarget,
  useNavigationUtils,
} from '../../common/utils/useNavigationUtils';
import {
  COACH_ROUTE,
  HOME_ROUTE, RESOURCES_ROUTE,
  isOnboarding, MYACCOUNT_PREFERENCES_ROUTE, MYACCOUNT_ROUTE, MYACCOUNT_SETTINGS_ROUTE,
  PARTNERS_ROUTE, SAVED_ROUTE, WALLET_ROUTE,
  SEARCH_ROUTE,
  MYACCOUNT_CONTACT_US_ROUTE,
  MYACCOUNT_FAQ_ROUTE,
  MYACCOUNT_SOLUTION_ROUTE,
  MYACCOUNT_MEMBERSHIPS_ROUTE,
  TRAINING_ROUTE,
  DESIGN_SERVICES_ROUTE,
  ADVISORY_BOARDS_ROUTE,
} from '../../routes/namedRoutes';
import { useSolutionOrg } from '../../config/useSolutionOrg';
import { useIsDesktop } from '../../common/hooks/useResponsive';
import { DashboardMobileLayout } from './Home/DashboardMobileLayout';
import { selectMemberInformation } from '../../store/slices/onboardingSlice';
import { selectCompanyRewardsWallet } from '../../store/slices/dashboardSlice';
import { getCompanyRewardsWallet } from '../../store/thunks/dashboardThunk';
import { useOptimizelyFlag } from '../../common/hooks/useOptimizelyFlag';
import { useCustomEventTracking } from '../../common/useCustomEventTracking';
import { useServiceNationUrl } from '../../common/utils/useServiceNationUrl';
import { useServiceNationLinkInterceptor } from '../../common/hooks/useServiceNationLinkInterceptor';
import { AccessResource, useHasAccess } from '../../common/hooks/useHasAccess';
import { canUpgradeTier, isPaidTier } from '../../common/utils/MembershipTierAccess';
import { SubscriptionTier } from '../../common/models/Subscription';
import {
  DashboardTopNavLayout, TopNavAccountItem, TopNavItem,
} from './Home/DashboardTopNavLayout';

// Top navigation (edgeHomeNewDesign) uses outlined icons only, some differing from the sidebar.
// Top navigation icon overrides: outlined at rest, filled on the current page.
const TOP_NAV_ICONS: Record<string, { Outline: typeof SvgIcon; Filled: typeof SvgIcon }> = {
  resources: { Outline: ArticleOutlinedIcon, Filled: ArticleIcon },
  partners: { Outline: PartnerIcon, Filled: FilledPartnerIcon },
  wallet: { Outline: WalletIcon, Filled: FilledWalletIcon },
  events: { Outline: SchoolOutlinedIcon, Filled: SchoolIcon },
  designServices: { Outline: DesignServicesOutlinedIcon, Filled: DesignServicesIcon },
  onDemandTraining: { Outline: OndemandVideoOutlinedIcon, Filled: OndemandVideoIcon },
};

const TIER_NAME_KEYS: Record<SubscriptionTier, string> = {
  [SubscriptionTier.EDGE]: 'partnerDetails.tierName.edge',
  [SubscriptionTier.AM]: 'partnerDetails.tierName.association',
  [SubscriptionTier.SRT]: 'partnerDetails.tierName.roundtable',
  [SubscriptionTier.RCC]: 'partnerDetails.tierName.coalition',
  [SubscriptionTier.CC]: 'partnerDetails.tierName.coalition',
  [SubscriptionTier.ADV]: 'partnerDetails.tierName.advanced',
  [SubscriptionTier.ADVP]: 'partnerDetails.tierName.advanced',
  [SubscriptionTier.SNA]: 'partnerDetails.tierName.alliance',
  [SubscriptionTier.PIN]: 'partnerDetails.tierName.pinnacle',
};

export interface DashboardScreensLayoutProps {
  children: JSX.Element;
  navElems: {
    id?: string;
    name: string;
    path: string;
    Outline: typeof SvgIcon;
    Filled: typeof SvgIcon;
    isCurrent?: boolean;
    subMenuItems?: DashboardSubMenuItemsProps[];
    bottomFixPosition?: boolean;
    onClick?: () => void;
    secondaryLabel?: string;
    rightIcon?: typeof SvgIcon;
    onSecondaryLabelClick?: () => void;
  }[];
}

interface DashboardLayoutProps {
  children: JSX.Element;
}

export function DashboardLayout({
  children,
}: DashboardLayoutProps) {
  const isDesktop = useIsDesktop();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { solutionOrgName } = useSolutionOrg();
  const {
    company, firstName, lastName, email,
  } = useAppSelector(selectMemberInformation);
  const { trackEngagement } = useCustomEventTracking();
  const { openExternalLink } = useNavigationUtils();
  const { buildServiceNationUrl } = useServiceNationUrl();
  useServiceNationLinkInterceptor();
  const companyRewardsWallet = useAppSelector(selectCompanyRewardsWallet);
  const companyId = company?.id;
  const isFreeTier = !isPaidTier(company?.subscription?.subscriptionTier);
  const canUpgrade = canUpgradeTier(company?.subscription?.subscriptionTier);

  const {
    decision: communityLinkDecision,
    isDecisionReady: isCommunityLinkDecisionReady,
  } = useOptimizelyFlag({
    flag: ServiceNationCommunityLinkFlag,
  });

  const {
    decision: membershipsPageDecision,
    isDecisionReady: isMembershipsPageDecisionReady,
  } = useOptimizelyFlag({
    flag: MembershipsPageFlag,
  });

  const {
    decision: eventsLinkDecision,
    isDecisionReady: isEventsLinkDecisionReady,
  } = useOptimizelyFlag({
    flag: ServiceNationEventsLinkFlag,
  });

  const {
    decision: designServicesLinkDecision,
    isDecisionReady: isDesignServicesLinkDecisionReady,
  } = useOptimizelyFlag({
    flag: ServiceNationDesignServicesLinkFlag,
  });

  const {
    decision: onDemandTrainingLinkDecision,
    isDecisionReady: isOnDemandTrainingLinkDecisionReady,
  } = useOptimizelyFlag({
    flag: ServiceNationOnDemandTrainingLinkFlag,
  });

  const {
    decision: iframeEventTrainingPageDecision,
    isDecisionReady: isIframeEventTrainingPageDecisionReady,
  } = useOptimizelyFlag({
    flag: IframeSNEventTrainingPageFlag,
  });
  const isEventTrainingPageIframed = isIframeEventTrainingPageDecisionReady &&
    !!iframeEventTrainingPageDecision?.enabled;

  const {
    decision: iframeDesignServicesPageDecision,
    isDecisionReady: isIframeDesignServicesPageDecisionReady,
  } = useOptimizelyFlag({
    flag: IframeSNDesignServicesPageFlag,
  });
  const isDesignServicesPageIframed = isIframeDesignServicesPageDecisionReady &&
    !!iframeDesignServicesPageDecision?.enabled;

  const {
    decision: snSettingsLinkDecision,
    isDecisionReady: isSNSettingsLinkDecisionReady,
  } = useOptimizelyFlag({
    flag: SNSettingsLinkFlag,
  });
  const isSNSettingsLinkEnabled = isSNSettingsLinkDecisionReady &&
    !!snSettingsLinkDecision?.enabled;
  // SN native members' Settings live on Service Nation, so it opens there in a new tab
  const isServiceNationMember = solutionOrgName === ServiceNationSolutionOrgName;

  const {
    decision: advisoryBoardsLinkDecision,
    isDecisionReady: isAdvisoryBoardsLinkDecisionReady,
  } = useOptimizelyFlag({
    flag: ServiceNationAdvisoryBoardsLinkFlag,
  });

  const {
    decision: aiCoachDecision,
    isDecisionReady: isAiCoachDecisionReady,
  } = useOptimizelyFlag({
    flag: AiCoachFlag,
  });

  const {
    decision: serviceNationWalletDecision,
    isDecisionReady: isServiceNationWalletDecisionReady,
  } = useOptimizelyFlag({
    flag: ServiceNationWalletFlag,
  });

  const {
    decision: useEdgeHomeForServiceNationUsersDecision,
    isDecisionReady: isUseEdgeHomeForServiceNationUsersDecisionReady,
  } = useOptimizelyFlag({
    flag: UseEdgeHomeForServiceNationUsersFlag,
  });

  const {
    decision: edgeHomeNewDesignDecision,
    isDecisionReady: isEdgeHomeNewDesignDecisionReady,
    loaderComponent: edgeHomeNewDesignLoaderComponent,
  } = useOptimizelyFlag({
    flag: EdgeHomeNewDesignFlag,
  });
  const isEdgeHomeNewDesignEnabled = isEdgeHomeNewDesignDecisionReady &&
    !!edgeHomeNewDesignDecision?.enabled;

  const openCommunityLink = async () => {
    trackEngagement('Navigation', 'Click', 'CommunityLink');
    try {
      openExternalLink(await buildServiceNationUrl('community'));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to generate Service Nation SSO token', error);
    }
  };

  const trackAdvisoryBoardsLinkClick = () => trackEngagement('Navigation', 'Click', 'AdvisoryBoardsLink');

  const trackEventsLinkClick = () => trackEngagement('Navigation', 'Click', 'EventsLink');

  const openEventsLink = async () => {
    trackEventsLinkClick();
    try {
      openExternalLink(await buildServiceNationUrl('events'));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to generate Service Nation SSO token', error);
    }
  };

  const trackDesignServicesLinkClick = () => trackEngagement('Navigation', 'Click', 'DesignServicesLink');

  const openDesignServicesLink = async () => {
    trackDesignServicesLinkClick();
    try {
      openExternalLink(await buildServiceNationUrl('resource-hub'));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to generate Service Nation SSO token', error);
    }
  };

  const openOnDemandTrainingLink = async () => {
    trackEngagement('Navigation', 'Click', 'OnDemandTrainingLink');
    try {
      openExternalLink(await buildServiceNationUrl('academy'));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to generate Service Nation SSO token', error);
    }
  };

  const openSNSettingsLink = async () => {
    trackEngagement('Navigation', 'Click', 'SNSettingsLink');
    try {
      openExternalLink(await buildServiceNationUrl('settings'));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to generate Service Nation SSO token', error);
    }
  };

  const openBillingInformationLink = async () => {
    trackEngagement('Navigation', 'Click', 'BillingInformationLink');
    try {
      openExternalLink(await buildServiceNationUrl('billing'));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to generate Service Nation SSO token', error);
    }
  };

  useEffect(() => {
    dispatch(getTopTopics());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (companyId) {
      dispatch(getCompanyRewardsWallet(companyId));
    }
  }, [companyId, dispatch]);

  const walletBalanceLabel = useMemo(() => {
    const rawBalance = companyRewardsWallet?.balance;
    if (!rawBalance) return undefined;

    try {
      const formatted = new Decimal(rawBalance);
      if (formatted.lte(0)) return undefined;
      return `+$${formatted.toFixed(2)}`;
    } catch (error) {
      return undefined;
    }
  }, [companyRewardsWallet?.balance]);

  const handleLogout = () => (
    dispatch(logout({
      isOnboarding: isOnboarding(),
      navigate,
      customAfterLoginPath: '/',
    }))
  );

  const handleUpgradeBadgeClick = () => {
    trackEngagement('Navigation', 'Click', 'UpgradeBadge');
    navigate(MYACCOUNT_MEMBERSHIPS_ROUTE);
  };

  const OpenServiceNationLink = () => (
    openExternalLink(process.env.REACT_APP_SERVICE_NATION_URL || '/home', NavigationTarget.Webview)
  );

  const accountSubMenuItems: DashboardSubMenuItemsProps[] = [];

  const hasMembershipsPageAccess = useHasAccess(AccessResource.MembershipsPage);
  if (isMembershipsPageDecisionReady && membershipsPageDecision?.enabled &&
    hasMembershipsPageAccess) {
    accountSubMenuItems.push({
      title: 'Memberships',
      path: MYACCOUNT_MEMBERSHIPS_ROUTE,
      isCurrent: MYACCOUNT_MEMBERSHIPS_ROUTE === location.pathname,
    });
  }

  const hasAccountSettingsAccess = useHasAccess(AccessResource.AccountSettingsPage, {
    snSettingsLinkFlag: isSNSettingsLinkEnabled,
  });
  if (hasAccountSettingsAccess) {
    accountSubMenuItems.push(isServiceNationMember ? {
      title: `${t('navigation.settings')}`,
      path: '#',
      onClick: openSNSettingsLink,
      isCurrent: false,
      rightIcon: OpenInNewIcon,
    } : {
      title: `${t('navigation.settings')}`,
      path: MYACCOUNT_SETTINGS_ROUTE,
      isCurrent: MYACCOUNT_SETTINGS_ROUTE === location.pathname,
    });
  }

  if (!isFreeTier) {
    accountSubMenuItems.push(
      {
        title: `${t('navigation.billingInformation')}`,
        path: '#',
        onClick: openBillingInformationLink,
        isCurrent: false,
        rightIcon: OpenInNewIcon,
      },
    );
  }

  accountSubMenuItems.push(
    {
      title: `${t('navigation.preferences')}`,
      path: MYACCOUNT_PREFERENCES_ROUTE,
      isCurrent: MYACCOUNT_PREFERENCES_ROUTE === location.pathname,
    },
  );

  const hasFAQPageAccess = useHasAccess(AccessResource.FAQPage);
  if (hasFAQPageAccess) {
    accountSubMenuItems.push(
      {
        title: `${t('navigation.faq')}`,
        path: MYACCOUNT_FAQ_ROUTE,
        isCurrent: MYACCOUNT_FAQ_ROUTE === location.pathname,
      },
    );
  }

  accountSubMenuItems.push(
    {
      title: `${t('navigation.contactUs')}`,
      path: MYACCOUNT_CONTACT_US_ROUTE,
      isCurrent: MYACCOUNT_CONTACT_US_ROUTE === location.pathname,
    },
  );

  if (solutionOrgName === JOIST_DOMAIN) {
    accountSubMenuItems.push({
      title: `${t('navigation.joist')}`,
      path: MYACCOUNT_SOLUTION_ROUTE,
      isCurrent: MYACCOUNT_SOLUTION_ROUTE === location.pathname,
    });
  }

  const fullNavElems = [
    {
      id: 'snHome',
      name: 'Home',
      path: '#',
      Outline: HomeIcon,
      Filled: FilledHomeIcon,
      isHidden: !useHasAccess(AccessResource.SNHomePage, {
        useEdgeHomeForSNFlag: isUseEdgeHomeForServiceNationUsersDecisionReady &&
          useEdgeHomeForServiceNationUsersDecision?.enabled,
      }),
      isCurrent: false,
      onClick: OpenServiceNationLink,
    },
    {
      id: 'home',
      name: 'Home',
      path: HOME_ROUTE,
      Outline: HomeIcon,
      Filled: FilledHomeIcon,
      isCurrent: false,
      isHidden: !useHasAccess(AccessResource.HomePage, {
        useEdgeHomeForSNFlag: isUseEdgeHomeForServiceNationUsersDecisionReady &&
          useEdgeHomeForServiceNationUsersDecision?.enabled,
      }),
    },
    {
      id: 'resources',
      name: 'Resources',
      // Its own route, so the nav tells it apart from Home whatever page Home renders.
      path: RESOURCES_ROUTE,
      Outline: FeedOutlinedIcon,
      Filled: FeedIcon,
      isCurrent: false,
      // Top navigation always lists Resources under More. The sidebar keeps the access rule.
      isHidden: !useHasAccess(AccessResource.ResourcesPage, {
        useEdgeHomeForSNFlag: isUseEdgeHomeForServiceNationUsersDecisionReady &&
          useEdgeHomeForServiceNationUsersDecision?.enabled,
      }) && !isEdgeHomeNewDesignEnabled,
    },
    {
      id: 'saved',
      name: 'Saved',
      path: SAVED_ROUTE,
      Outline: BookmarkBorderOutlinedIcon,
      Filled: BookmarkIcon,
      isHidden: false,
    },
    {
      id: 'coaching',
      name: 'Coaching',
      path: COACH_ROUTE,
      Outline: AssistantOutlinedIcon,
      Filled: AssistantIcon,
      // Gated behind the aiCoach Optimizely flag — hidden until the flag
      // resolves and is enabled for the user.
      isHidden: !(isAiCoachDecisionReady && aiCoachDecision?.enabled),
    },
    {
      id: 'wallet',
      name: 'Wallet',
      path: WALLET_ROUTE,
      Outline: WalletIcon,
      Filled: FilledWalletIcon,
      // Show for non-SN members always. For SN members (any tier, including
      // free/EDGE), only once the serviceNationWallet flag is enabled AND
      // (SN-190) SN has granted the member historic_rebate_access.
      isHidden: !useHasAccess(
        AccessResource.WalletPage,
        {
          serviceNationWalletFlag: isServiceNationWalletDecisionReady &&
            serviceNationWalletDecision?.enabled,
        },
      ),
      secondaryLabel: walletBalanceLabel,
    },
    {
      id: 'partners',
      name: 'Partners',
      path: PARTNERS_ROUTE,
      Outline: PartnerIcon,
      Filled: FilledPartnerIcon,
    },
    {
      id: 'community',
      name: `${t('navigation.community')}`,
      path: '#',
      Outline: PeopleAltOutlinedIcon,
      Filled: PeopleAltIcon,
      isHidden: (!useHasAccess(AccessResource.CommunityPage, {
        useEdgeHomeForSNFlag: isUseEdgeHomeForServiceNationUsersDecisionReady &&
          useEdgeHomeForServiceNationUsersDecision?.enabled,
      }) ||
        !((isCommunityLinkDecisionReady && communityLinkDecision?.enabled))),
      onClick: openCommunityLink,
      rightIcon: OpenInNewIcon,
    },
    {
      id: 'advisoryBoards',
      name: `${t('navigation.advisoryBoards')}`,
      path: ADVISORY_BOARDS_ROUTE,
      Outline: GroupsOutlinedIcon,
      Filled: GroupsIcon,
      isHidden: (!useHasAccess(AccessResource.AdvisoryBoardsPage) ||
        !(isAdvisoryBoardsLinkDecisionReady && advisoryBoardsLinkDecision?.enabled)),
      onClick: trackAdvisoryBoardsLinkClick,
    },
    {
      id: 'events',
      // Top navigation renames Events to Training
      name: isEdgeHomeNewDesignEnabled ? `${t('navigation.training')}` : `${t('navigation.events')}`,
      // With iframeSNEventTrainingPage the page is embedded in-app instead of opening a new tab
      path: isEventTrainingPageIframed ? TRAINING_ROUTE : '#',
      Outline: EventOutlinedIcon,
      Filled: EventIcon,
      isHidden: (!useHasAccess(AccessResource.EventsPage, {
        useEdgeHomeForSNFlag: isUseEdgeHomeForServiceNationUsersDecisionReady &&
          useEdgeHomeForServiceNationUsersDecision?.enabled,
      }) ||
        !(isEventsLinkDecisionReady && eventsLinkDecision?.enabled)),
      onClick: isEventTrainingPageIframed ? trackEventsLinkClick : openEventsLink,
      rightIcon: isEventTrainingPageIframed ? undefined : OpenInNewIcon,
    },
    {
      id: 'designServices',
      name: `${t('navigation.designServices')}`,
      // With iframeSNDesignServicesPage the page is embedded in-app instead of opening a new tab
      path: isDesignServicesPageIframed ? DESIGN_SERVICES_ROUTE : '#',
      Outline: DesignServicesOutlinedIcon,
      Filled: DesignServicesIcon,
      isHidden: (!useHasAccess(AccessResource.DesignServicesPage, {
        useEdgeHomeForSNFlag: isUseEdgeHomeForServiceNationUsersDecisionReady &&
          useEdgeHomeForServiceNationUsersDecision?.enabled,
      }) ||
        !(isDesignServicesLinkDecisionReady && designServicesLinkDecision?.enabled)),
      onClick: isDesignServicesPageIframed ? trackDesignServicesLinkClick : openDesignServicesLink,
      rightIcon: isDesignServicesPageIframed ? undefined : OpenInNewIcon,
    },
    {
      id: 'onDemandTraining',
      name: `${t('navigation.onDemandTraining')}`,
      path: '#',
      Outline: SchoolOutlinedIcon,
      Filled: SchoolIcon,
      isHidden: (!useHasAccess(AccessResource.OnDemandTrainingPage, {
        useEdgeHomeForSNFlag: isUseEdgeHomeForServiceNationUsersDecisionReady &&
          useEdgeHomeForServiceNationUsersDecision?.enabled,
      }) ||
        !(isOnDemandTrainingLinkDecisionReady && onDemandTrainingLinkDecision?.enabled)),
      onClick: openOnDemandTrainingLink,
      rightIcon: OpenInNewIcon,
    },
    {
      id: 'account',
      name: 'Account',
      path: MYACCOUNT_ROUTE,
      Outline: KeyboardArrowRightIcon,
      Filled: FilledAccountIcon,
      subMenuItems: accountSubMenuItems,
      isHidden: !useHasAccess(AccessResource.AccountPage, {
        useEdgeHomeForSNFlag: isUseEdgeHomeForServiceNationUsersDecisionReady &&
          useEdgeHomeForServiceNationUsersDecision?.enabled,
      }),
      bottomFixPosition: true,
      secondaryLabel: ((isMembershipsPageDecisionReady && membershipsPageDecision?.enabled) &&
        canUpgrade) ? t('memberships.upgradeBadge') : undefined,
      onSecondaryLabelClick: ((isMembershipsPageDecisionReady &&
        membershipsPageDecision?.enabled) && canUpgrade) ?
        handleUpgradeBadgeClick :
        undefined,
    },
    {
      id: 'logout',
      name: 'Log Out',
      path: '#',
      Outline: LogoutIcon,
      Filled: ExpandMoreIcon,
      bottomFixPosition: true,
      onClick: handleLogout,
      isHidden: !useHasAccess(AccessResource.Logout, {
        useEdgeHomeForSNFlag: isUseEdgeHomeForServiceNationUsersDecisionReady &&
          useEdgeHomeForServiceNationUsersDecision?.enabled,
      }),
    },
    {
      id: 'search',
      name: 'Search',
      path: SEARCH_ROUTE,
      Outline: HomeIcon,
      Filled: FilledHomeIcon,
      // This is for mapping the extra column only.
      isHidden: true,
      isCurrent: false,
    },
  ];

  // Exact URL matches (paths with a query) win over path matches.
  // Only visible items are matched (Search is hidden but still mapped).
  const matchableNavElems = fullNavElems.filter((elem) => !elem.isHidden || elem.id === 'search');
  const currentUrl = `${location.pathname}${location.search}`;
  const currentRoute = matchableNavElems.find((elem) => elem.path.includes('?') && elem.path === currentUrl) ??
    matchableNavElems.find((elem) => {
      const [path] = elem.path.split('?');
      return location.pathname === path || location.pathname.startsWith(`${path}/`);
    });
  // Without the new design /home is the resources feed, so when Home is hidden that page
  // highlights Resources rather than nothing.
  const resourcesNavElem = matchableNavElems.find((elem) => elem.id === 'resources');
  if (!currentRoute && !isEdgeHomeNewDesignEnabled && location.pathname === HOME_ROUTE &&
    resourcesNavElem) {
    resourcesNavElem.isCurrent = true;
  }
  if (currentRoute) {
    // Search results are resources, so the top navigation highlights Resources. The sidebar
    // (old design) keeps highlighting Home, where its search bar lives.
    if (currentRoute.name === 'Search') {
      const searchParentRoute = isEdgeHomeNewDesignEnabled ?
        resourcesNavElem :
        fullNavElems.find((elem) => !elem.isHidden && elem.path === HOME_ROUTE);
      if (searchParentRoute) searchParentRoute.isCurrent = true;
    } else {
      currentRoute.isCurrent = true;
    }
  }

  const navElems = fullNavElems.filter((elem) => !elem.isHidden);

  // Wait for the flag so the left navigation doesn't flash before the top navigation loads in
  if (!isEdgeHomeNewDesignDecisionReady) {
    return edgeHomeNewDesignLoaderComponent;
  }

  if (isEdgeHomeNewDesignEnabled) {
    const toTopNavItem = (elem: typeof navElems[number]): TopNavItem => ({
      id: elem.id,
      name: elem.name,
      path: elem.path,
      Icon: (TOP_NAV_ICONS[elem.id] ?? elem).Outline,
      FilledIcon: (TOP_NAV_ICONS[elem.id] ?? elem).Filled,
      isCurrent: elem.isCurrent,
      // Like the sidebar, items with a real path navigate after their onClick runs
      onClick: elem.onClick && elem.path !== '#' ?
        () => {
          elem.onClick?.();
          navigate(elem.path);
        } :
        elem.onClick,
    });
    const pickNavItems = (ids: string[]) => navElems
      .filter((elem) => ids.includes(elem.id))
      .map(toTopNavItem);

    const isMembershipsPageEnabled = isMembershipsPageDecisionReady &&
      !!membershipsPageDecision?.enabled && hasMembershipsPageAccess;
    // Account pages route to Notfound for members without AccountPage access, so hide their links.
    // navElems has hidden items filtered out, so the Account item is only present with access.
    const hasAccountPageAccess = navElems.some((elem) => elem.id === 'account');
    const topNavAccountItems: TopNavAccountItem[] = [...pickNavItems(['wallet'])];
    if (hasAccountPageAccess && isMembershipsPageEnabled) {
      topNavAccountItems.push({
        id: 'memberships',
        name: `${t('navigation.memberships')}`,
        path: MYACCOUNT_MEMBERSHIPS_ROUTE,
        Icon: PersonOutlineIcon,
        FilledIcon: FilledAccountIcon,
        isCurrent: MYACCOUNT_MEMBERSHIPS_ROUTE === location.pathname,
        actionLabel: isFreeTier ? `${t('navigation.upgradeNow')}` : undefined,
        onActionClick: isFreeTier ? handleUpgradeBadgeClick : undefined,
      });
    }
    if (hasAccountPageAccess && hasAccountSettingsAccess) {
      topNavAccountItems.push({
        id: 'settings',
        name: `${t('navigation.settings')}`,
        Icon: SettingsOutlinedIcon,
        FilledIcon: SettingsIcon,
        ...isServiceNationMember ? {
          path: '#',
          onClick: openSNSettingsLink,
        } : {
          path: MYACCOUNT_SETTINGS_ROUTE,
          isCurrent: MYACCOUNT_SETTINGS_ROUTE === location.pathname,
        },
      });
    }
    if (hasAccountPageAccess && !isFreeTier) {
      topNavAccountItems.push({
        id: 'billing',
        name: `${t('navigation.billingInformation')}`,
        path: '#',
        Icon: CreditCardOutlinedIcon,
        onClick: openBillingInformationLink,
      });
    }
    if (hasAccountPageAccess) {
      topNavAccountItems.push({
        id: 'preferences',
        name: `${t('navigation.preferences')}`,
        path: MYACCOUNT_PREFERENCES_ROUTE,
        Icon: TuneIcon,
        isCurrent: MYACCOUNT_PREFERENCES_ROUTE === location.pathname,
      });
    }
    if (hasAccountPageAccess && hasFAQPageAccess) {
      topNavAccountItems.push({
        id: 'faq',
        name: `${t('navigation.faq')}`,
        path: MYACCOUNT_FAQ_ROUTE,
        Icon: HelpOutlineIcon,
        FilledIcon: HelpIcon,
        isCurrent: MYACCOUNT_FAQ_ROUTE === location.pathname,
      });
    }
    if (hasAccountPageAccess) {
      topNavAccountItems.push(
        {
          id: 'support',
          name: `${t('navigation.support')}`,
          path: MYACCOUNT_CONTACT_US_ROUTE,
          Icon: SupportOutlinedIcon,
          FilledIcon: SupportIcon,
          isCurrent: MYACCOUNT_CONTACT_US_ROUTE === location.pathname,
        },
      );
    }
    if (hasAccountPageAccess && solutionOrgName === JOIST_DOMAIN) {
      topNavAccountItems.push({
        id: 'joist',
        name: `${t('navigation.joist')}`,
        path: MYACCOUNT_SOLUTION_ROUTE,
        Icon: LinkOutlinedIcon,
        FilledIcon: LinkIcon,
        isCurrent: MYACCOUNT_SOLUTION_ROUTE === location.pathname,
      });
    }
    topNavAccountItems.push(...pickNavItems(['logout']).map((item) => ({
      ...item,
      isDestructive: true,
      hasDividerBefore: true,
    })));

    const tierNameKey = company?.subscription?.subscriptionTier &&
      TIER_NAME_KEYS[company.subscription.subscriptionTier];

    return (
      <DashboardTopNavLayout
        navItems={pickNavItems([
          'snHome', 'home', 'coaching', 'partners', 'community', 'advisoryBoards', 'events',
        ])}
        moreItems={pickNavItems(['resources', 'designServices', 'onDemandTraining'])}
        accountItems={topNavAccountItems}
        accountName={[firstName, lastName].filter(Boolean).join(' ')}
        accountEmail={email ?? ''}
        tierLabel={tierNameKey ? `${t(tierNameKey)}` : undefined}
      >
        {children}
      </DashboardTopNavLayout>
    );
  }

  return (
    <>
      {(isDesktop ?
        <DashboardDesktopLayout navElems={navElems}>
          {children}
        </DashboardDesktopLayout> :
        <Box>
          <DashboardMobileLayout navElems={navElems}>
            {children}
          </DashboardMobileLayout>
        </Box>
      )}
    </>
  );
}
